// src/pages/PageDetail.js
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getTitle, getImage, getDescriptionHtml, getExcerpt, localePath,
} from "../utils/content";
import { fetchPageById, fetchRelatedPages } from "../utils/fetchers";
import Seo, { absoluteUrl } from "../components/Seo";

/** Full-width hero fallback for pages with no image */
function PageHeroFallback({ title = "" }) {
  const initials = title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  return (
    <div className="detail-hero detail-hero--fallback" aria-hidden="true">
      <svg
        viewBox="0 0 900 380"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id="pageHeroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1a1200" />
            <stop offset="55%" stopColor="#221700" />
            <stop offset="100%" stopColor="#0f0d00" />
          </linearGradient>
          <radialGradient id="pageHeroGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--gold, #c9a84c)" stopOpacity="0.07" />
            <stop offset="100%" stopColor="var(--gold, #c9a84c)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width="900" height="380" fill="url(#pageHeroGrad)" />
        <rect width="900" height="380" fill="url(#pageHeroGlow)" />

        {/* Document icon */}
        <g transform="translate(360, 60)" stroke="var(--gold, #c9a84c)" strokeWidth="1.2" fill="none" opacity="0.13">
          <rect x="0" y="0" width="160" height="220" rx="6" />
          <path d="M120 0 l40 40 l-40 0 Z" fill="var(--gold, #c9a84c)" opacity="0.25" />
          <line x1="24" y1="70" x2="136" y2="70" />
          <line x1="24" y1="92" x2="136" y2="92" />
          <line x1="24" y1="114" x2="110" y2="114" />
          <line x1="24" y1="136" x2="130" y2="136" />
          <line x1="24" y1="158" x2="100" y2="158" />
          <line x1="24" y1="180" x2="118" y2="180" />
        </g>

        {/* Horizontal rules */}
        <line x1="60" y1="340" x2="840" y2="340" stroke="var(--gold, #c9a84c)" strokeWidth="0.5" opacity="0.2" />
        <line x1="60" y1="40" x2="840" y2="40" stroke="var(--gold, #c9a84c)" strokeWidth="0.5" opacity="0.2" />

        {/* Corner brackets */}
        <g stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.5">
          <polyline points="60,60 60,40 80,40" fill="none" />
          <polyline points="820,40 840,40 840,60" fill="none" />
          <polyline points="60,320 60,340 80,340" fill="none" />
          <polyline points="820,340 840,340 840,320" fill="none" />
        </g>

        {/* Initials */}
        {initials && (
          <text
            x="450"
            y="200"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="88"
            fontFamily="Georgia, 'Times New Roman', serif"
            letterSpacing="14"
            opacity="0.2"
          >
            {initials}
          </text>
        )}

        <text
          x="450"
          y="260"
          textAnchor="middle"
          fill="var(--gold, #c9a84c)"
          fontSize="11"
          fontFamily="Georgia, serif"
          letterSpacing="6"
          opacity="0.3"
        >
          CCMA · DAJC
        </text>
      </svg>
    </div>
  );
}

export default function PageDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [page, setPage] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroError, setHeroError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPage(null);
    setHeroError(false);

    Promise.all([fetchPageById(id), fetchRelatedPages(id, 8)])
      .then(([p, r]) => {
        if (cancelled) return;
        setPage(p);
        setRelated(r);
        setLoading(false);
      })
      .catch(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <section style={{ minHeight: "60vh" }}>
        <div className="loading"><div className="spinner" /></div>
      </section>
    );
  }
  if (!page) {
    return (
      <section style={{ minHeight: "60vh" }}>
        <div className="container empty">
          <h3>{t("common.not_found_title")}</h3>
          <p>{t("common.not_found_text")}</p>
          <Link to={localePath(lang)} className="btn btn-ghost" style={{ marginTop: 24 }}>
            {t("common.back_home")}
          </Link>
        </div>
      </section>
    );
  }

  const title = getTitle(page, lang, 200);
  const heroImg = getImage(page, lang);
  const html = getDescriptionHtml(page, lang);

  const showFallback = !heroImg || heroError;

  return (
    <>
      <Seo
        path={`page/${page.id}`}
        title={title}
        description={getExcerpt(page, lang, 30)}
        image={heroImg && !heroError ? heroImg : undefined}
        type="article"
        publishedTime={page.starts_at || page.created_at}
        breadcrumb={[
          { name: t("common.home"), path: "" },
          { name: t("menu.ressources"), path: "ressources" },
          { name: title, path: `page/${page.id}` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description: getExcerpt(page, lang, 30),
          inLanguage: lang,
          datePublished: page.starts_at || page.created_at,
          dateModified: page.updated_at || page.starts_at || page.created_at,
          image: heroImg && !heroError ? heroImg : undefined,
          mainEntityOfPage: absoluteUrl(`page/${page.id}`, lang),
          publisher: { "@type": "Organization", name: t("brand.full") },
        }}
      />

      <section className="page-header">
        <div className="container">
          <div className="page-header__inner">
            <div className="page-header__crumbs">
              <Link to={localePath(lang)}>{t("common.home")}</Link>
              <span>/</span>
              <Link to={localePath(lang, "ressources")}>{t("menu.ressources")}</Link>
              <span>/</span>
              <span>{title.slice(0, 60)}{title.length > 60 ? "…" : ""}</span>
            </div>
            <h1>{title}</h1>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          <div className="detail-layout">
            <article className="detail-content">

              {/* Hero image with fallback */}
              {showFallback ? (
                <PageHeroFallback title={title} />
              ) : (
                <div className="detail-hero">
                  <img
                    src={heroImg}
                    alt={title}
                    onError={() => setHeroError(true)}
                  />
                </div>
              )}

              {html ? (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              ) : null}

              <div style={{ marginTop: 56, paddingTop: 32, borderTop: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                <Link to={localePath(lang, "ressources")} className="btn btn-ghost">
                  ← {t("pages.view_all")}
                </Link>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="dajc-icon-btn"
                    onClick={() => window.print()}
                    aria-label="Print"
                  >
                    <i className="bi bi-printer" />
                  </button>
                  <a
                    className="dajc-icon-btn"
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank" rel="noopener noreferrer"
                  >
                    <i className="bi bi-linkedin" />
                  </a>
                  <a
                    className="dajc-icon-btn"
                    href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(window.location.href)}`}
                  >
                    <i className="bi bi-envelope" />
                  </a>
                </div>
              </div>
            </article>

            <aside className="detail-aside">
              <div className="aside-card" style={{ background: "var(--ink)", color: "var(--ivory)", borderColor: "var(--ink)" }}>
                <h4 style={{ color: "var(--gold-soft)" }}>{t("ccma.eyebrow")}</h4>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(250,246,236,0.78)", margin: "0 0 18px" }}>
                  {t("ccma.intro")}
                </p>
                <Link
                  to={localePath(lang, "page/2")}
                  className="btn btn-gold"
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {t("hero.cta_primary")}
                </Link>
              </div>

              <div className="aside-card">
                <h4>{t("footer.ccma_title")}</h4>
                <ul>
                  {related.length === 0 && (
                    <li style={{ color: "var(--muted)", fontSize: 14 }}>—</li>
                  )}
                  {related.map((r) => (
                    <li key={r.id}>
                      <Link to={localePath(lang, `page/${r.id}/${r.slug || ""}`)}>
                        {getTitle(r, lang, 70)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="aside-card">
                <h4>{t("contact.title")}</h4>
                <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 10 }}>
                  <i className="bi bi-envelope" style={{ marginRight: 8, color: "var(--gold-deep)" }} />
                  <a href={`mailto:${t("contact.email_site")}`} style={{ color: "var(--ink)" }}>{t("contact.email_site")}</a>
                </div>
                <div style={{ fontSize: 14, color: "var(--muted)" }}>
                  <i className="bi bi-telephone" style={{ marginRight: 8, color: "var(--gold-deep)" }} />
                  <bdi>{t("contact.phone")}</bdi>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}